/**
 * Lightweight entity catalog for SEO. Each entry represents a real-world
 * entity (Person, Place, Event, DefinedTerm, Organization) referenced by
 * pSEO pages. Used to inject `about` and `mentions` arrays into JSON-LD
 * with sameAs to Wikipedia/Wikidata so Google can resolve canonical entities.
 */

export interface EntityRef {
  "@type": "Person" | "Event" | "Place" | "Organization" | "DefinedTerm" | "Thing";
  name: string;
  description?: string;
  sameAs?: string[];
  /** Optional extra fields (e.g. birthDate for Person). */
  [key: string]: unknown;
}

/** Map slug (hari penting) to its primary entity. */
export const HARI_ENTITIES: Record<string, EntityRef> = {
  kartini: {
    "@type": "Person",
    name: "Raden Adjeng Kartini",
    description: "Pahlawan nasional Indonesia, pelopor emansipasi perempuan dan pendidikan kaum wanita.",
    birthDate: "1879-04-21",
    deathDate: "1904-09-17",
    sameAs: [
      "https://id.wikipedia.org/wiki/R.A._Kartini",
      "https://www.wikidata.org/wiki/Q234907",
    ],
  },
  buruh: {
    "@type": "Thing",
    name: "Hari Buruh Internasional",
    description: "Peringatan global gerakan buruh dan hak-hak pekerja, dirayakan setiap 1 Mei.",
    sameAs: [
      "https://id.wikipedia.org/wiki/Hari_Buruh",
      "https://www.wikidata.org/wiki/Q188408",
    ],
  },
  ibu: {
    "@type": "Thing",
    name: "Hari Ibu Indonesia",
    description: "Peringatan nasional yang berakar dari Kongres Perempuan Indonesia I tahun 1928 di Yogyakarta.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Ibu"],
  },
  ayah: {
    "@type": "Thing",
    name: "Hari Ayah Nasional",
    description: "Hari peringatan untuk menghormati peran ayah dalam keluarga Indonesia, dideklarasikan di Solo tahun 2006.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Ayah_Nasional"],
  },
  guru: {
    "@type": "Thing",
    name: "Hari Guru Nasional",
    description: "Peringatan berdirinya Persatuan Guru Republik Indonesia (PGRI) pada 25 November 1945.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Guru_Nasional"],
  },
  "batik-nasional": {
    "@type": "Thing",
    name: "Hari Batik Nasional",
    description: "Peringatan pengakuan UNESCO atas batik Indonesia sebagai Warisan Kemanusiaan untuk Budaya Lisan dan Nonbendawi pada 2 Oktober 2009.",
    sameAs: [
      "https://id.wikipedia.org/wiki/Hari_Batik_Nasional",
      "https://www.wikidata.org/wiki/Q1129072",
    ],
  },
  pahlawan: {
    "@type": "Thing",
    name: "Hari Pahlawan",
    description: "Peringatan pertempuran Surabaya 10 November 1945 dan jasa para pahlawan nasional.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Pahlawan"],
  },
  kemerdekaan: {
    "@type": "Thing",
    name: "Hari Kemerdekaan Republik Indonesia",
    description: "Peringatan proklamasi kemerdekaan Indonesia oleh Soekarno dan Mohammad Hatta pada 17 Agustus 1945.",
    sameAs: [
      "https://id.wikipedia.org/wiki/Hari_Kemerdekaan_Indonesia",
      "https://www.wikidata.org/wiki/Q3296825",
    ],
  },
  "sumpah-pemuda": {
    "@type": "Thing",
    name: "Hari Sumpah Pemuda",
    description: "Peringatan ikrar Sumpah Pemuda yang dirumuskan dalam Kongres Pemuda II di Batavia, 28 Oktober 1928.",
    sameAs: ["https://id.wikipedia.org/wiki/Sumpah_Pemuda"],
  },
  valentine: {
    "@type": "Thing",
    name: "Hari Valentine",
    description: "Hari kasih sayang internasional yang diperingati setiap 14 Februari.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Valentine"],
  },
  natal: {
    "@type": "Thing",
    name: "Hari Natal",
    description: "Hari raya umat Kristiani memperingati kelahiran Yesus Kristus, dirayakan setiap 25 Desember.",
    sameAs: ["https://id.wikipedia.org/wiki/Natal"],
  },
  "tahun-baru": {
    "@type": "Thing",
    name: "Tahun Baru Masehi",
    description: "Pergantian tahun pada kalender Gregorian, diperingati setiap 1 Januari.",
    sameAs: ["https://id.wikipedia.org/wiki/Tahun_Baru"],
  },
  "perempuan-internasional": {
    "@type": "Thing",
    name: "Hari Perempuan Internasional",
    description: "Peringatan global hak-hak perempuan dan kesetaraan gender, dirayakan setiap 8 Maret.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Perempuan_Internasional"],
  },
  "anak-nasional": {
    "@type": "Thing",
    name: "Hari Anak Nasional",
    description: "Peringatan hak-hak anak Indonesia, ditetapkan oleh Keppres No. 44 Tahun 1984 dan diperingati setiap 23 Juli.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Anak_Nasional"],
  },
  "lahir-pancasila": {
    "@type": "Thing",
    name: "Hari Lahir Pancasila",
    description: "Peringatan pidato Soekarno 1 Juni 1945 tentang dasar negara Indonesia, Pancasila.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Lahir_Pancasila"],
  },
  "pendidikan-nasional": {
    "@type": "Thing",
    name: "Hari Pendidikan Nasional",
    description: "Peringatan kelahiran Ki Hadjar Dewantara, Bapak Pendidikan Indonesia, pada 2 Mei.",
    sameAs: ["https://id.wikipedia.org/wiki/Hari_Pendidikan_Nasional"],
  },
};

/** Tools / DefinedTerm catalog (Weton, Neptu, dst). */
export const TERM_ENTITIES: Record<string, EntityRef> = {
  weton: {
    "@type": "DefinedTerm",
    name: "Weton",
    description: "Hari kelahiran berdasarkan kombinasi tujuh hari dalam seminggu (Saptawara) dan lima hari pasaran Jawa (Pancawara).",
    sameAs: ["https://id.wikipedia.org/wiki/Weton"],
  },
  neptu: {
    "@type": "DefinedTerm",
    name: "Neptu",
    description: "Nilai numerik gabungan hari dan pasaran dalam primbon Jawa, digunakan untuk perhitungan watak dan rejeki.",
    sameAs: ["https://id.wikipedia.org/wiki/Neptu"],
  },
  pasaran: {
    "@type": "DefinedTerm",
    name: "Pasaran",
    description: "Lima hari dalam pekan pasaran Jawa: Legi, Pahing, Pon, Wage, dan Kliwon.",
    sameAs: ["https://id.wikipedia.org/wiki/Pasaran"],
  },
  wuku: {
    "@type": "DefinedTerm",
    name: "Wuku",
    description: "Sistem siklus 30 minggu (210 hari) dalam kalender tradisional Jawa dan Bali (Pawukon).",
    sameAs: ["https://id.wikipedia.org/wiki/Pawukon"],
  },
  pancasuda: {
    "@type": "DefinedTerm",
    name: "Pancasuda",
    description: "Klasifikasi watak primbon Jawa berdasarkan neptu: Sri, Lungguh, Gedhong, Lara, Pati.",
  },
};

/** Build a small WebSite + Organization isPartOf object. */
export function publisherRef(origin: string) {
  return {
    "@type": "Organization",
    name: "Hari.co.id",
    url: origin + "/",
    logo: { "@type": "ImageObject", url: origin + "/apple-touch-icon.png" },
  };
}

/** Common prayer / Islamic entities used by jadwal sholat, imsak, buka puasa. */
export function prayerEntities(): EntityRef[] {
  return [
    { "@type": "DefinedTerm", name: "Sholat", sameAs: ["https://id.wikipedia.org/wiki/Salat"] },
    { "@type": "DefinedTerm", name: "Subuh", sameAs: ["https://id.wikipedia.org/wiki/Salat_Subuh"] },
    { "@type": "DefinedTerm", name: "Maghrib", sameAs: ["https://id.wikipedia.org/wiki/Salat_Magrib"] },
    { "@type": "DefinedTerm", name: "Isya", sameAs: ["https://id.wikipedia.org/wiki/Salat_Isya"] },
    { "@type": "DefinedTerm", name: "Iftar (Buka Puasa)", sameAs: ["https://id.wikipedia.org/wiki/Ifthar"] },
    { "@type": "DefinedTerm", name: "Imsak", sameAs: ["https://id.wikipedia.org/wiki/Imsak"] },
    { "@type": "Thing", name: "Ramadhan", sameAs: ["https://id.wikipedia.org/wiki/Ramadan"] },
    { "@type": "DefinedTerm", name: "Hisab", sameAs: ["https://id.wikipedia.org/wiki/Hisab"] },
    { "@type": "DefinedTerm", name: "Rukyat", sameAs: ["https://id.wikipedia.org/wiki/Rukyatulhilal"] },
    { "@type": "Organization", name: "Kementerian Agama Republik Indonesia", sameAs: ["https://id.wikipedia.org/wiki/Kementerian_Agama_Republik_Indonesia", "https://kemenag.go.id"] },
  ];
}

/** Calendar-related entities (Masehi, Hijriah, Jawa, Bali, libur nasional). */
export function calendarEntities(): EntityRef[] {
  return [
    { "@type": "DefinedTerm", name: "Kalender Masehi", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Gregorius"] },
    { "@type": "DefinedTerm", name: "Kalender Hijriah", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Hijriah"] },
    { "@type": "DefinedTerm", name: "Kalender Jawa", sameAs: ["https://id.wikipedia.org/wiki/Kalender_Jawa"] },
    { "@type": "DefinedTerm", name: "Kalender Bali (Pawukon)", sameAs: ["https://id.wikipedia.org/wiki/Pawukon"] },
    { "@type": "Thing", name: "Hari Libur Nasional Indonesia", sameAs: ["https://id.wikipedia.org/wiki/Hari_libur_di_Indonesia"] },
  ];
}

/** Hijri month entities (12 bulan). */
export function hijriMonthEntities(): EntityRef[] {
  return [
    { "@type": "DefinedTerm", name: "Muharram", sameAs: ["https://id.wikipedia.org/wiki/Muharam"] },
    { "@type": "DefinedTerm", name: "Safar", sameAs: ["https://id.wikipedia.org/wiki/Safar"] },
    { "@type": "DefinedTerm", name: "Rabiul Awal", sameAs: ["https://id.wikipedia.org/wiki/Rabiulawal"] },
    { "@type": "DefinedTerm", name: "Rabiul Akhir", sameAs: ["https://id.wikipedia.org/wiki/Rabiulakhir"] },
    { "@type": "DefinedTerm", name: "Jumadil Awal", sameAs: ["https://id.wikipedia.org/wiki/Jumadilawal"] },
    { "@type": "DefinedTerm", name: "Jumadil Akhir", sameAs: ["https://id.wikipedia.org/wiki/Jumadilakhir"] },
    { "@type": "DefinedTerm", name: "Rajab", sameAs: ["https://id.wikipedia.org/wiki/Rajab"] },
    { "@type": "DefinedTerm", name: "Syaban", sameAs: ["https://id.wikipedia.org/wiki/Syakban"] },
    { "@type": "DefinedTerm", name: "Ramadhan", sameAs: ["https://id.wikipedia.org/wiki/Ramadan"] },
    { "@type": "DefinedTerm", name: "Syawal", sameAs: ["https://id.wikipedia.org/wiki/Syawal"] },
    { "@type": "DefinedTerm", name: "Zulkaidah", sameAs: ["https://id.wikipedia.org/wiki/Zulkaidah"] },
    { "@type": "DefinedTerm", name: "Zulhijah", sameAs: ["https://id.wikipedia.org/wiki/Zulhijah"] },
  ];
}
